'use client';

import { useState } from 'react';

interface Props {
  startPage?: number;
  category?: string | null;
  excludeIds?: string[];
}

type RichTextNode = { text?: string } | string;
type Category = {
  uid?: string;
  id?: string;
  data?: { name?: string };
  name?: string;
} | null;
type BlogData = {
  card_title?: RichTextNode[] | string;
  title?: RichTextNode[] | string;
  image?: { url?: string } | null;
  category?: Category;
  all_categories?: Array<{ category?: Category }>;
  [k: string]: unknown;
};

export default function PopularLoadMoreServer({
  startPage = 1,
  category,
  excludeIds = [],
}: Props) {
  const [page, setPage] = useState(startPage);
  const [loading, setLoading] = useState(false);
  const [excludedIds, setExcludedIds] = useState<string[]>(excludeIds);

  const loadMore = async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('limit', '3');
      if (category) {
        params.append('category', category);
      }
      if (excludedIds.length > 0) {
        params.append('exclude', excludedIds.join(','));
      }

      const res = await fetch(`/api/blog/popular/html?${params.toString()}`);
      const json = await res.json();
      if (json?.results && json.results.length > 0) {
        const newIds: string[] = [];
        const grid = document.getElementById('grid-container');
        const newsletter = grid?.querySelector(
          '[data-newsletter]',
        ) as HTMLElement | null;
        // The newsletter lives inside the inner grid (grid > div.grid ... > [data-newsletter])
        const innerGrid = newsletter?.parentElement;
        if (newsletter && innerGrid) {
          json.results.forEach(
            (it: {
              id?: string;
              uid?: string;
              date?: string | null;
              image?: string | null;
              data?: unknown;
              categoryUid?: string | null;
              categoryName?: string | null;
              author?: {
                name?: string;
                avatar?: string | null;
                uid?: string;
              };
              readDuration?: number | null;
            }) => {
              // Track this ID
              if (it.id) {
                newIds.push(it.id);
              }

              const wrapper = document.createElement('div');
              // Use classes that mirror BlogCard 'new-simple' to match visuals
              wrapper.className =
                'flex flex-col rounded-[20px] overflow-hidden text-start text-white h-full';
              // inline background to match BlogCard new-simple style
              wrapper.setAttribute(
                'style',
                'background: linear-gradient(to right, rgba(255,255,255,0.15), rgba(3,191,158,0.15), rgba(255,255,255,0.15))',
              );

              // Use raw data shape to compute title, image and category like server does
              const rawData = (it.data ?? {}) as { blog?: BlogData } & BlogData;
              const blogData = (rawData.blog ?? rawData) as BlogData;

              // Title may be rich text
              let titleText = '';
              const titleField = blogData.card_title ?? blogData.title ?? '';
              if (Array.isArray(titleField)) {
                titleText = titleField
                  .map((n: RichTextNode) =>
                    typeof n === 'string'
                      ? n
                      : n && typeof n === 'object'
                        ? (n.text ?? '')
                        : '',
                  )
                  .filter(Boolean)
                  .join(' ');
              } else {
                titleText = String(titleField || '');
              }
              const safeTitle = titleText
                .replace(/</g, '&lt;')
                .replace(/"/g, '&quot;');

              // Image
              let safeImage: string =
                'https://images.prismic.io/devsucom2024/ZyO63a8jQArT0F9p_DevsuLogo.png';
              if (
                blogData &&
                typeof blogData === 'object' &&
                'image' in blogData
              ) {
                const img = blogData.image;
                if (
                  img &&
                  typeof img === 'object' &&
                  typeof img.url === 'string'
                ) {
                  safeImage = String(img.url);
                }
              }
              if (!safeImage && it.image) {
                safeImage = String(it.image);
              }

              // Category: prefer API-provided fields, then fall back to blogData shapes
              const apiCategoryUid = it.categoryUid ?? null;
              const apiCategoryName = it.categoryName ?? null;

              let firstCategory: Category = null;
              if (blogData?.category) {
                firstCategory = blogData.category;
              } else if (
                blogData?.all_categories &&
                Array.isArray(blogData.all_categories)
              ) {
                firstCategory = blogData.all_categories[0]?.category ?? null;
              } else if (rawData?.category) {
                firstCategory = rawData.category;
              }

              const computedCategoryUid =
                firstCategory?.uid ?? firstCategory?.id ?? null;
              const computedCategoryName =
                firstCategory?.data?.name ?? firstCategory?.name ?? '';

              const finalCategoryUid = apiCategoryUid ?? computedCategoryUid;
              const finalCategoryName =
                (apiCategoryName ?? computedCategoryName) || '';
              const safeCategoryName = (finalCategoryName || '').toUpperCase();
              const categoryHtml = safeCategoryName
                ? finalCategoryUid
                  ? `<a href="/resources-center?category=${finalCategoryUid}" class="text-xs text-[#05F273] font-semibold tracking-wider no-underline">${safeCategoryName}</a>`
                  : `<span class="text-xs text-[#05F273] font-semibold tracking-wider">${safeCategoryName}</span>`
                : '';

              // Extract author information
              const authorName = it.author?.name ?? 'Devsu';
              const authorAvatar =
                it.author?.avatar ??
                'https://images.prismic.io/devsucom2024/ZyO63a8jQArT0F9p_DevsuLogo.png?auto=format%2Ccompress&fit=max&w=1080';
              const authorUid = it.author?.uid ?? 'devsu';
              const readDuration = it.readDuration ?? null;

              // Format date
              const formattedDate = it.date
                ? new Date(it.date).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                : '';

              const durationText = readDuration ? `• ${readDuration} min` : '';

              // Optimize image URL with imgix parameters
              const optimizedImage = safeImage
                ? safeImage.includes('?')
                  ? `${safeImage}&fit=crop&ar=16:9&q=75&auto=format,compress`
                  : `${safeImage}?fit=crop&ar=16:9&q=75&auto=format,compress`
                : '';

              // Optimize author avatar with imgix parameters
              const optimizedAvatar = authorAvatar.includes('?')
                ? `${authorAvatar}&fit=crop&ar=1:1&q=75&w=86&auto=format,compress`
                : `${authorAvatar}?fit=crop&ar=1:1&q=75&w=86&auto=format,compress`;

              wrapper.innerHTML = `
              ${
                optimizedImage
                  ? `
                <div class="relative h-48">
                  <a href="/resources-center/${it.uid}">
                    <img src="${optimizedImage}" alt="${safeTitle}" class="h-full w-full object-cover rounded-t-[20px]" loading="lazy" />
                  </a>
                  <div class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div class="absolute top-4 right-4 text-white"></div>
                </div>
              `
                  : ''
              }
              <div class="p-6 flex flex-col justify-between h-full">
                <div>
                  <a href="/resources-center/${it.uid}"><h3 class="text-xl font-bold text-white mb-2">${safeTitle}</h3></a>
                </div>
                <div class="flex flex-col gap-4">
                  <p class="text-[#05F273] text-sm font-semibold uppercase">
                    ${categoryHtml}
                  </p>
                  <a href="/resources-center/authors/${authorUid}" class="flex items-center space-x-3 no-underline text-inherit group cursor-pointer">
                    <div class="w-[43px] h-[42px] rounded-[8px] overflow-hidden flex-shrink-0">
                      <img src="${optimizedAvatar}" alt="${authorName}" class="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div class="flex flex-col text-sm">
                      <div class="font-semibold group-hover:underline">${authorName}</div>
                      <div class="flex items-center space-x-2">
                        ${formattedDate ? `<span>${formattedDate}</span>` : ''}
                        ${durationText ? `<span>${durationText}</span>` : ''}
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            `;
              innerGrid.insertBefore(wrapper, newsletter);
            },
          );
        }
        // Add newly loaded IDs to excluded list
        setExcludedIds((prev) => [...prev, ...newIds]);
        setPage((p) => p + 1);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center w-full mt-8">
      <button
        className={`px-6 py-3 font-bold text-white rounded-lg bg-primary-darker-green-black border border-solid border-primary-darker-green-black w-max transition-all hover:rounded-[100px] hover:border-t hover:border-white hover:bg-[radial-gradient(55.37%_55.22%_at_50.29%_89.58%,#05f273_0%,#03bf9e_100%)] hover:shadow-[0px_0px_0px_3px_rgba(3,194,155,0.2),0px_0px_60px_0px_#05f36c] active:bg-white disabled:bg-opacity-50 ${loading ? 'opacity-60 pointer-events-none' : ''}`}
        onClick={loadMore}
        disabled={loading}
        aria-label="Load more popular posts"
      >
        {loading ? 'Loading…' : 'Load more'}
      </button>
    </div>
  );
}
